import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CreateProgram } from "../CreateProgram";
import { EditProgram } from "../EditProgram";
import { AdvertisingProgramsTable } from "../AdvertisingProgramsTable";
import { useProgramsWithActiveAdCounts } from "./useProgramsWithActiveAdCounts";
import { Button } from "../../../core/basic/Button";
import { Breadcrumb, Breadcrumbs } from "../../../core/basic/Breadcrumbs";
import { Stack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { type ProgramEditorView } from "../InlinePrograms";
import { useSimpleCan } from "../../../../config/casl/casl";
import { usePrograms } from "../../../../modules/programs/api/usePrograms";
import { toProgramFields } from "../../../../modules/programs/programFormValues";
import { type Program } from "../../../../zodios/apiDefinitions/api/programs";

interface TableProps {
  onOpenProgram: (id: number) => void;
  onCreateProgram: () => void;
}

const InlineBiddingProgramsTable = ({ onOpenProgram, onCreateProgram }: TableProps) => {
  const { t } = useTranslation(["programs"]);
  const title = t(($) => $.programs["Bidding Programs"]);
  const canEdit = useSimpleCan("update", "Ad");

  const { data, isLoading } = usePrograms({ engine: "bidding" });
  const programs = useProgramsWithActiveAdCounts(data);

  return (
    <AdvertisingProgramsTable
      isLoading={isLoading}
      programs={programs}
      title={title}
      persistKey="adPlanBiddingProgramsTable"
      onOpenProgram={onOpenProgram}
      canEdit={canEdit}
      showActiveAds
      showClone
      toolbarAction={<Button onPress={onCreateProgram}>{t(($) => $.programs.footer.Create)}</Button>}
    />
  );
};

type NewBiddingProgram = ReturnType<typeof toProgramFields> & { template_type: string; engine: "bidding" };

interface Props {
  defaultCreateProgram?: boolean;
  /** Rendered inside the ad plan modal: no breadcrumbs, and cancelling stays on the create form. */
  modalDisplay?: boolean;
  hidden?: boolean;
  parentError?: string | null;
  /** Receives the created program, or with `deferProgramCreation` the program to create later. */
  onActivate?: (program: Program | NewBiddingProgram) => void;
  deferProgramCreation?: boolean;
}

export const InlineBiddingPrograms = ({
  defaultCreateProgram,
  modalDisplay,
  hidden = false,
  parentError,
  onActivate,
  deferProgramCreation = false,
}: Props) => {
  const { t } = useTranslation(["programs"]);
  const [view, setView] = useState<ProgramEditorView>({ kind: defaultCreateProgram ? "create" : "list" });
  const showList = useCallback(() => setView({ kind: "list" }), []);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const queryClient = useQueryClient();
  const title = t(($) => $.programs["Bidding Programs"]);

  const openProgram = useCallback((programId: number) => setView({ kind: "edit", programId }), []);

  if (hidden) {
    return null;
  }

  return (
    <Stack gap="large">
      {!modalDisplay && view.kind !== "list" && (
        <Breadcrumbs>
          <Breadcrumb onPress={showList}>{title}</Breadcrumb>
          <Breadcrumb>
            {view.kind === "create" ? t(($) => $.programs.footer.Create) : t(($) => $.programs.Edit)}
          </Breadcrumb>
        </Breadcrumbs>
      )}
      {parentError && !isFormDirty && (
        <p role="alert" className={css({ color: "error" })}>
          {parentError}
        </p>
      )}
      {view.kind === "list" && (
        <InlineBiddingProgramsTable onOpenProgram={openProgram} onCreateProgram={() => setView({ kind: "create" })} />
      )}
      {view.kind === "create" && (
        <CreateProgram
          engine="bidding"
          deferProgramCreation={deferProgramCreation}
          onSuccess={(program) => {
            onActivate?.(program);
            showList();
            void queryClient.invalidateQueries({ queryKey: ["programs"] });
          }}
          onSubmit={(values, templateType) => {
            onActivate?.({
              ...toProgramFields(values, templateType),
              template_type: templateType,
              engine: "bidding",
            });
          }}
          overrideSize={modalDisplay ? "xs" : null}
          onFormChange={() => {
            if (!isFormDirty) {
              setIsFormDirty(true);
            }
          }}
        />
      )}
      {view.kind === "edit" && (
        <EditProgram
          programId={view.programId}
          onSuccess={() => {
            showList();
            void queryClient.invalidateQueries({ queryKey: ["programs"] });
          }}
          onCancel={showList}
        />
      )}
    </Stack>
  );
};
